import React, { useEffect, useState, useRef } from 'react'
import {
  Form,
  FileWrapper,
  Button,
  Field,
  FieldLabel,
  Input
} from './styles'
import { Icon } from '../../../assets/icons'
import { FaMicrophone, FaStop } from 'react-icons/fa'
import { useGameWrapper, useGame } from '../../../contexts'
import { getTeam } from '../../../utils/team_helpers'
import { makePostMissionAnswer } from '../../../../main/usecases/mission-answer/post-mission-answer-factory'
import { addAllowFullScreen, replaceInstructionsTags } from '../../../utils/mission_helpers'
import { Container, Image, Instructions, ActionButtons, GroupButtons } from '../styles'
import { getAssetUrl } from '../../../utils/assets_helpers'
import { t } from '../../../adapters/translate/translate'
import { isMobile } from '../../../utils/general'
import { replaceHtmlEntity } from '../../../utils/text_replace'
import CountdownTimer from './timer-display'

const MAX_AUDIO_DURATION = 100
const SAMPLE_RATE = 16000
const CHANNELS = 1
const BIT_DEPTH = 16

interface MediaTrackConstraints {
  channelCount: number
  sampleRate: number
  sampleSize: number
  echoCancellation: boolean
  noiseSuppression: boolean
  autoGainControl: boolean
}

interface AudioRecorderConfig {
  mimeType: string
  audioBitsPerSecond: number
}

const AudioMission = () => {
  const [instructions, setInstructions] = useState<string|null>('')
  const [description, setDescription] = useState('')
  const [isRecording, setIsRecording] = useState(false)
  const [audioFile, setAudioFile] = useState<File | null>(null)
  const [audioURL, setAudioURL] = useState('')
  const [isFilePicked, setIsFilePicked] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const mediaRecorder = useRef<MediaRecorder | null>(null)
  const audioChunks = useRef<Blob[]>([])
  const timerInterval = useRef<ReturnType<typeof setInterval> | null>(null)
  const team = getTeam()
  const { timeout, currentMission, submitCallback, doSubmit, submitButtonIsEnabled, handleToggleStatusSubmitButton, setTimePaused } = useGameWrapper()
  const { game } = useGame()
  const [color, setColor] = useState<any>('#fff')
  const mobile = isMobile()

  const resetMission = () => {
    setIsFilePicked(false)
    setAudioURL('')
    setAudioFile(null)
    setDescription('')
    setRecordingTime(0)
    if (timerInterval.current) {
      clearInterval(timerInterval.current)
    }
  }

  const startRecording = async (): Promise<void> => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Your browser does not support audio recording')
      }

      const constraints: MediaTrackConstraints = {
        channelCount: CHANNELS,
        sampleRate: SAMPLE_RATE,
        sampleSize: BIT_DEPTH,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: constraints })
      const mimeType = MediaRecorder.isTypeSupported('audio/wav') ? 'audio/wav' : 'audio/webm'

      try {
        const config: AudioRecorderConfig = {
          mimeType,
          audioBitsPerSecond: 32000
        }
        mediaRecorder.current = new MediaRecorder(stream, config)
      } catch (e) {
        mediaRecorder.current = new MediaRecorder(stream)
      }

      audioChunks.current = []
      mediaRecorder.current.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          audioChunks.current.push(event.data)
        }
      }

      mediaRecorder.current.onstop = async () => {
        if (mimeType === 'audio/wav') {
          await processWavFile(audioChunks.current)
        } else {
          const webmBlob = new Blob(audioChunks.current, { type: 'audio/webm' })
          await convertWebmToWav(webmBlob)
        }
      }

      mediaRecorder.current.start(1000)
      setIsRecording(true)
      startTimer()
    } catch (error) {
      console.error('Recording error:', error)
      setIsRecording(false)
    }
  }

  const processWavFile = async (chunks: Blob[]): Promise<void> => {
    const audioBlob = new Blob(chunks, { type: 'audio/wav' })
    await compressAndSaveWav(audioBlob)
  }

  const startTimer = (): void => {
    timerInterval.current = setInterval(() => {
      setRecordingTime(prev => {
        if (prev >= MAX_AUDIO_DURATION) {
          stopRecording()
          return prev
        }
        return prev + 1
      })
    }, 1000)
  }

  const convertWebmToWav = async (webmBlob: Blob): Promise<void> => {
    try {
      const arrayBuffer = await webmBlob.arrayBuffer()
      const audioContext = new AudioContext({ sampleRate: SAMPLE_RATE })
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
      const wavBuffer = audioBufferToWav(audioBuffer)
      const wavBlob = new Blob([wavBuffer], { type: 'audio/wav' })
      await compressAndSaveWav(wavBlob)
    } catch (error) {
      console.error('Error converting to WAV:', error)
    }
  }

  const compressAndSaveWav = async (wavBlob: Blob): Promise<void> => {
    try {
      const audioFile = new File([wavBlob], 'audio-recording.wav', {
        type: 'audio/wav',
        lastModified: Date.now()
      })

      const url = URL.createObjectURL(wavBlob)
      setAudioFile(audioFile)
      setAudioURL(url)
      setIsFilePicked(true)
      setIsRecording(false)
    } catch (error) {
      console.error('Error compressing WAV:', error)
    }
  }

  const audioBufferToWav = (buffer: AudioBuffer): ArrayBuffer => {
    const length = buffer.length
    const bytesPerSample = BIT_DEPTH / 8
    const blockAlign = CHANNELS * bytesPerSample
    const byteRate = SAMPLE_RATE * blockAlign
    const dataSize = length * blockAlign
    const bufferSize = 44 + dataSize
    const arrayBuffer = new ArrayBuffer(bufferSize)
    const view = new DataView(arrayBuffer)

    const writeString = (view: DataView, offset: number, string: string): void => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i))
      }
    }

    // WAV Header
    writeString(view, 0, 'RIFF')
    view.setUint32(4, 36 + dataSize, true)
    writeString(view, 8, 'WAVE')
    writeString(view, 12, 'fmt ')
    view.setUint32(16, 16, true)
    view.setUint16(20, 1, true)
    view.setUint16(22, CHANNELS, true)
    view.setUint32(24, SAMPLE_RATE, true)
    view.setUint32(28, byteRate, true)
    view.setUint16(32, blockAlign, true)
    view.setUint16(34, BIT_DEPTH, true)
    writeString(view, 36, 'data')
    view.setUint32(40, dataSize, true)

    // PCM Data
    let offset = 44
    const channelData = buffer.getChannelData(0)
    for (let i = 0; i < length; i++) {
      const sample = Math.max(-1, Math.min(1, channelData[i]))
      const value = sample < 0 ? sample * 0x8000 : sample * 0x7FFF
      view.setInt16(offset, value, true)
      offset += bytesPerSample
    }

    return arrayBuffer
  }

  const stopRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      mediaRecorder.current.stop()
      mediaRecorder.current.stream.getTracks().forEach(track => track.stop())
    }
    if (timerInterval.current) {
      clearInterval(timerInterval.current)
    }
  }

  const formatTime = (seconds: number) => {
    const maxDurationWithBuffer = MAX_AUDIO_DURATION - 10
    const remainingTime = maxDurationWithBuffer - seconds
    if (remainingTime <= 0) {
      return '0:00'
    }
    const minutes = Math.floor(remainingTime / 60)
    const remainingSeconds = remainingTime % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  async function sendAnswer () {
    try {
      if (team.id && audioFile) {
        const formData = new FormData()
        formData.append('mission', currentMission?.uuid || '')
        formData.append('audioFile', audioFile)
        formData.append('team', team.id)
        formData.append('subtitle', description)

        if (timeout) {
          formData.append('status', 'timeout')
        } else {
          formData.append('status', 'responded')
        }

        const res = await makePostMissionAnswer('/audio_upload').post(formData)
        return res
      }
    } catch (error) {
      console.error('AudioMission.sendAnswer', error)
    }
  }

  function handleEnableButton () {
    handleToggleStatusSubmitButton(!!audioFile)
  }

  useEffect(() => {
    if (!currentMission) return
    const formattedInstructions = replaceInstructionsTags({ mission: currentMission, points: currentMission.pointsIfCorrect })
    setInstructions(addAllowFullScreen(formattedInstructions))
  }, [currentMission])

  useEffect(() => {
    handleEnableButton()
  }, [audioFile, isFilePicked])

  useEffect(() => {
    if (doSubmit && submitButtonIsEnabled) {
      submitCallback(sendAnswer)
    }
  }, [doSubmit, submitButtonIsEnabled])

  useEffect(() => {
    if (timeout) {
      submitCallback(sendAnswer)
    }
  }, [timeout])

  useEffect(() => {
    resetMission()
  }, [currentMission])

  useEffect(() => {
    setTimePaused(false)
    if (game) { setColor(game?.textColor || '#fff') }
  }, [game])

  useEffect(() => {
    const div = document.getElementById('instructions')
    if (div) {
      div.innerHTML = replaceHtmlEntity(div.innerHTML)
    }
  }, [instructions])

  return (
    <Container>
      { currentMission?.icon && <Image src={getAssetUrl('icon', String(currentMission?.icon))} /> }
      {!!instructions && <Instructions id="instructions" dangerouslySetInnerHTML={
        { __html: instructions }
      } />}

      {mobile
        ? (
          <ActionButtons>
            <button
              onClick={isRecording ? stopRecording : startRecording}
              style={{ borderColor: game?.baseColor }}
            >
              {isRecording
                ? (
                  <>
                    <FaStop color='#FF0000' style={{ margin: '3px 0' }}/>
                    <CountdownTimer time={formatTime(recordingTime)} baseColor={game?.baseColor} />
                  </>)
                : (
                  <>
                    <FaMicrophone color={game?.baseColor}/>
                    <h3 style={{ color: game?.baseColor }}>{t('Record audio')}</h3>
                  </>)
              }
            </button>
          </ActionButtons>
          )
        : (
        <GroupButtons>
          <button
            onClick={isRecording ? stopRecording : startRecording}
            style={{ borderColor: game?.baseColor }}
          >
            {isRecording
              ? (
                <>
                  <FaStop color='#FF0000' style={{ margin: '3px 0' }}/>
                  <CountdownTimer time={formatTime(recordingTime)} baseColor={game?.baseColor} />
                </>
                )
              : (
                <>
                  <FaMicrophone fill={game?.baseColor}/>
                  <h3 style={{ color: game?.baseColor }}>{t('Record audio')}</h3>
                </>
                )
            }
          </button>
        </GroupButtons>)}

      <Form $isOpen={isFilePicked}>
        <FileWrapper>
          <audio src={audioURL} controls />
          <Button onClick={resetMission}>
            <Icon name="x"/>
          </Button>
        </FileWrapper>

        <Field>
          <FieldLabel style={{ color }}>{t('Audio description')}</FieldLabel>
          <Input
            value={description}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
            placeholder={`${t('Enter here')}...`}
          />
        </Field>
      </Form>
    </Container>
  )
}

export default AudioMission
